import { useEffect, useState } from 'react';
import { createItem, deleteItem, fetchList, fetchLookups, updateItem } from './api';
import { canWriteEmpresaData } from '../../auth/session';
import CrudModal from '../../components/layout/CrudModal';
import { formatCurrencyBR, normalizeCurrencyInputBR, parseCurrencyBR } from '../../utils/numberBR';

const EMPTY = {
  obra_id: '',
  valor_venda: '',
  data_venda: '',
  tipo_recebimento: 'financiamento',
  comprador_pessoa_id: '',
  observacoes: '',
};

const ConstrutorVendasPage = () => {
  const canWrite = canWriteEmpresaData();
  const [items, setItems] = useState([]);
  const [lookups, setLookups] = useState({ obras: [], pessoas: [] });
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [obraFilter, setObraFilter] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    const [list, lk] = await Promise.all([fetchList('/api/construtor/vendas', { obra_id: obraFilter, q, page, pageSize: 10 }), fetchLookups()]);
    setItems(Array.isArray(list?.items) ? list.items : []);
    setTotal(Number(list?.total || 0));
    setLookups({ obras: lk?.obras || [], pessoas: lk?.pessoas || [] });
  };

  useEffect(() => {
    load();
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      valor_venda: parseCurrencyBR(form.valor_venda) ?? 0,
      comprador_pessoa_id: form.comprador_pessoa_id || null,
    };
    if (editingId) {
      await updateItem('/api/construtor/vendas', editingId, payload);
    } else {
      await createItem('/api/construtor/vendas', payload);
    }
    setEditingId('');
    setForm(EMPTY);
    setShowFormModal(false);
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir venda?')) return;
    await deleteItem('/api/construtor/vendas', id);
    await load();
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      obra_id: item.obra_id || '',
      valor_venda: item.valor_venda == null ? '' : formatCurrencyBR(item.valor_venda),
      data_venda: String(item.data_venda || '').slice(0, 10),
      tipo_recebimento: item.tipo_recebimento || 'financiamento',
      comprador_pessoa_id: item.comprador_pessoa_id || '',
      observacoes: item.observacoes || '',
    });
    setShowFormModal(true);
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendas</h1>
        {canWrite && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditingId('');
              setForm(EMPTY);
              setShowFormModal(true);
            }}
          >
            Incluir Venda
          </button>
        )}
      </div>

      {canWrite && (
        <CrudModal
          open={showFormModal}
          title={editingId ? 'Editar Venda' : 'Nova Venda'}
          onClose={() => {
            setShowFormModal(false);
            setEditingId('');
            setForm(EMPTY);
          }}
          maxWidthClass="max-w-5xl"
        >
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Dados da Venda</p>
              <p className="text-xs text-gray-500 mt-1">Registre o valor de venda e a forma de recebimento.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="form-label">Obra</label>
                <select className="form-select" value={form.obra_id} onChange={(e) => setForm({ ...form, obra_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {lookups.obras.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Valor da Venda (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.valor_venda}
                  onChange={(e) => setForm({ ...form, valor_venda: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
              <div>
                <label className="form-label">Data da Venda</label>
                <input className="form-input" type="date" value={form.data_venda} onChange={(e) => setForm({ ...form, data_venda: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Tipo de Recebimento</label>
                <select className="form-select" value={form.tipo_recebimento} onChange={(e) => setForm({ ...form, tipo_recebimento: e.target.value })}>
                  <option value="financiamento">Financiamento</option>
                  <option value="a_vista">A vista</option>
                  <option value="parcelado">Parcelado</option>
                </select>
              </div>
              <div>
                <label className="form-label">Comprador</label>
                <select className="form-select" value={form.comprador_pessoa_id} onChange={(e) => setForm({ ...form, comprador_pessoa_id: e.target.value })}>
                  <option value="">Opcional</option>
                  {lookups.pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
              <div className="md:col-span-5">
                <label className="form-label">Observacoes</label>
                <input className="form-input" placeholder="Detalhes complementares da venda" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditingId('');
                  setForm(EMPTY);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingId ? 'Atualizar Venda' : 'Salvar Venda'}</button>
            </div>
          </form>
        </CrudModal>
      )}

      <div className="card">
        <div className="card-header flex items-center gap-3">
          <select className="form-select max-w-sm" value={obraFilter} onChange={(e) => setObraFilter(e.target.value)}>
            <option value="">Todas obras</option>
            {lookups.obras.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
          </select>
          <input className="form-input max-w-sm" placeholder="Buscar obra/comprador..." value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-secondary" onClick={() => { setPage(1); load(); }}>Filtrar</button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Obra</th>
                  <th>Valor</th>
                  <th>Recebimento</th>
                  <th>Comprador</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{String(it.data_venda).slice(0, 10)}</td>
                    <td>{it.obra_nome}</td>
                    <td>R$ {Number(it.valor_venda || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{it.tipo_recebimento}</td>
                    <td>{it.comprador_nome || '-'}</td>
                    <td>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEdit(it)}>Editar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDelete(it.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-6">Sem dados</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body border-t flex items-center justify-between">
          <span className="text-sm text-gray-600">Pagina {page} de {totalPages} ({total} registros)</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Proxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstrutorVendasPage;
